import { SearchResultLink, useKeyboardHighlighting } from '@atlassian/search-dialog';
import React, { useCallback } from 'react';
import { useSearchSessionId } from '../../common/search-session-provider';
import { addQuery } from '../../utils/url-utils';
import { defaultResultRenderer } from './default-result-renderer';
import { useSearchResultAnalyticsCallbacks } from './use-search-result-analytics-callbacks';
var DefaultSearchResultItemWrapper = function DefaultSearchResultItemWrapper(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/React.createElement(React.Fragment, null, children);
};

/**
 * Intended to be used for search results that are not links
 */
var SearchResultItemKeyboardHiglightWrapper = function SearchResultItemKeyboardHiglightWrapper(_ref2) {
  var searchResultId = _ref2.searchResultId,
    children = _ref2.children;
  var _useKeyboardHighlight = useKeyboardHighlighting({
      searchResultId: searchResultId
    }),
    isKeyboardHighlighted = _useKeyboardHighlight.isKeyboardHighlighted,
    ref = _useKeyboardHighlight.ref;
  return /*#__PURE__*/React.createElement("div", {
    ref: ref
  }, children(isKeyboardHighlighted));
};

/**
 * This component displays a search result item in the search dialog. It
 * provides keyboard shortcuts, analytics, and displaying the search result
 * as a link. Notably, this component improves on its predecessor by allowing
 * the content of the search result item to be customised whilst still
 * retaining all of the above listed features.
 *
 * TODO: Should we explore the idea of allowing people to attach custom
 * analytics to search results? These would be two new fields searchResult
 *  - serachResult.analyticContext
 *  - searchResult.nonPrivacySafeAnalyticContext
 * These would get merged with the given analytics context. We might have some
 * issues with typing of analytics contexts though.
 */
export var SearchResultItem = function SearchResultItem(_ref3) {
  var _searchResult$linkCom;
  var isCollapsed = _ref3.isCollapsed,
    linkComponent = _ref3.linkComponent,
    productState = _ref3.productState,
    resultRenderer = _ref3.resultRenderer,
    resultWrapper = _ref3.resultWrapper,
    screen = _ref3.screen,
    searchResult = _ref3.searchResult,
    appendQueryParams = _ref3.appendQueryParams,
    _ref3$useAnalyticsOve = _ref3.useAnalyticsOverride,
    useAnalyticsOverride = _ref3$useAnalyticsOve === void 0 ? function () {
      return {};
    } : _ref3$useAnalyticsOve;
  var searchSessionId = useSearchSessionId();
  var _useSearchResultAnaly = useSearchResultAnalyticsCallbacks({
      screen: screen,
      actionSubject: 'searchResult'
    }),
    defaultOnHighlightCallback = _useSearchResultAnaly.onHighlightCallback,
    defaultOnSelectCallback = _useSearchResultAnaly.onSelectCallback,
    defaultOnContextMenuCallback = _useSearchResultAnaly.onContextMenuCallback;
  var _useAnalyticsOverride = useAnalyticsOverride({
      screen: screen
    }),
    overriddenOnHighlightCallback = _useAnalyticsOverride.onHighlightCallback,
    overriddenOnSelectCallback = _useAnalyticsOverride.onSelectCallback,
    overriddenOnContextMenuCallback = _useAnalyticsOverride.onContextMenuCallback;
  var url = appendQueryParams === undefined ? addQuery(searchResult.url, 'search_id', searchSessionId) : appendQueryParams(searchResult.url, searchSessionId);
  var SearchResultItemWrapper = resultWrapper || DefaultSearchResultItemWrapper;
  var searchResultContentRenderer = useCallback(function (isKeyboardHighlighted) {
    return resultRenderer ? resultRenderer({
      isCollapsed: isCollapsed,
      isKeyboardHighlighted: isKeyboardHighlighted,
      productState: productState,
      screen: screen,
      searchResult: searchResult
    }) : defaultResultRenderer({
      isCollapsed: isCollapsed,
      isKeyboardHighlighted: isKeyboardHighlighted,
      screen: screen,
      searchResult: searchResult
    });
  }, [isCollapsed, productState, resultRenderer, screen, searchResult]);
  return /*#__PURE__*/React.createElement(SearchResultItemWrapper, {
    url: url
  }, url ? /*#__PURE__*/React.createElement(SearchResultLink, {
    href: url,
    isCollapsed: isCollapsed,
    linkComponent: (_searchResult$linkCom = searchResult.linkComponent) !== null && _searchResult$linkCom !== void 0 ? _searchResult$linkCom : linkComponent,
    searchResultId: searchResult.id,
    onContextMenu: overriddenOnContextMenuCallback || defaultOnContextMenuCallback,
    onHighlighted: overriddenOnHighlightCallback || defaultOnHighlightCallback,
    onSelect: overriddenOnSelectCallback || defaultOnSelectCallback,
    "data-testid": "link-".concat(searchResult.id)
  }, function (isKeyboardHighlighted) {
    return searchResultContentRenderer(isKeyboardHighlighted);
  }) : /*#__PURE__*/React.createElement(SearchResultItemKeyboardHiglightWrapper, {
    searchResultId: searchResult.id
  }, function (isKeyboardHighlighted) {
    return searchResultContentRenderer(isKeyboardHighlighted);
  }));
};